(() => {
  document.documentElement.classList.add('liiv-js');

  const feedback = (() => {
    let node;

    function ensureNode() {
      if (node) return node;
      node = document.createElement('div');
      node.className = 'liiv-feedback';
      document.body.appendChild(node);
      return node;
    }

    return {
      show(message) {
        const target = ensureNode();
        target.textContent = message;
        target.classList.add('is-visible');
        window.clearTimeout(target._hideTimer);
        target._hideTimer = window.setTimeout(() => {
          target.classList.remove('is-visible');
        }, 2200);
      }
    };
  })();

  function updateTabGroup(root, value) {
    const triggers = root.querySelectorAll('[data-liiv-tab-trigger]');
    const panels = root.querySelectorAll('[data-liiv-tab-panel]');
    triggers.forEach((trigger) => {
      const active = trigger.dataset.liivTabTrigger === value;
      trigger.classList.toggle('is-active', active);
      trigger.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    panels.forEach((panel) => {
      panel.classList.toggle('is-active', panel.dataset.liivTabPanel === value);
    });

    window.requestAnimationFrame(() => {
      root
        .querySelectorAll('[data-liiv-tab-panel].is-active [data-liiv-carousel]')
        .forEach((carousel) => updateCarousel(carousel));
    });
  }

  function initTabs(scope = document) {
    scope.querySelectorAll('[data-liiv-tabs]').forEach((root) => {
      const activeTrigger =
        root.querySelector('[data-liiv-tab-trigger].is-active') || root.querySelector('[data-liiv-tab-trigger]');
      if (activeTrigger) {
        updateTabGroup(root, activeTrigger.dataset.liivTabTrigger);
      }
      root.querySelectorAll('[data-liiv-tab-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          updateTabGroup(root, trigger.dataset.liivTabTrigger);
        });
      });
    });
  }

  function updateCarousel(root) {
    const track = root.querySelector('[data-liiv-carousel-track]');
    const prev = root.querySelector('[data-liiv-carousel-prev]');
    const next = root.querySelector('[data-liiv-carousel-next]');
    if (!track) return;

    const maxScroll = Math.max(track.scrollWidth - track.clientWidth - 2, 0);
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= maxScroll;
  }

  function carouselStep(root) {
    const track = root.querySelector('[data-liiv-carousel-track]');
    const item = track?.querySelector('[data-liiv-carousel-item]');
    if (!track || !item) {
      return track?.clientWidth || 0;
    }
    const styles = window.getComputedStyle(track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return item.getBoundingClientRect().width + gap;
  }

  function initCarousels(scope = document) {
    scope.querySelectorAll('[data-liiv-carousel]').forEach((root) => {
      if (root.dataset.liivBound === 'true') return;
      root.dataset.liivBound = 'true';
      const track = root.querySelector('[data-liiv-carousel-track]');
      if (!track) return;

      root.querySelector('[data-liiv-carousel-prev]')?.addEventListener('click', () => {
        track.scrollBy({ left: -carouselStep(root), behavior: 'smooth' });
      });

      root.querySelector('[data-liiv-carousel-next]')?.addEventListener('click', () => {
        track.scrollBy({ left: carouselStep(root), behavior: 'smooth' });
      });

      track.addEventListener('scroll', () => updateCarousel(root), { passive: true });
      window.addEventListener('resize', () => updateCarousel(root));
      window.setTimeout(() => updateCarousel(root), 80);
    });
  }

  function initAnnouncements(scope = document) {
    scope.querySelectorAll('[data-liiv-announcements]').forEach((root) => {
      const slides = [...root.querySelectorAll('[data-liiv-announcement-slide]')];
      if (slides.length <= 1) return;
      const prev = root.querySelector('[data-liiv-announcement-prev]');
      const next = root.querySelector('[data-liiv-announcement-next]');
      let index = slides.findIndex((slide) => slide.classList.contains('is-active'));
      if (index < 0) index = 0;

      const render = () => {
        slides.forEach((slide, slideIndex) => {
          slide.classList.toggle('is-active', slideIndex === index);
        });
      };

      const step = (delta) => {
        index = (index + delta + slides.length) % slides.length;
        render();
      };

      prev?.addEventListener('click', () => step(-1));
      next?.addEventListener('click', () => step(1));

      window.setInterval(() => step(1), 5200);
      render();
    });
  }

  function initNavigation(scope = document) {
    scope.querySelectorAll('[data-liiv-nav]').forEach((root) => {
      const desktopItems = [...root.querySelectorAll('[data-liiv-desktop-item]')];
      const desktopTriggers = [...root.querySelectorAll('[data-liiv-nav-trigger]')];
      let desktopCloseTimer = null;
      const activateShopPane = (item, value) => {
        if (!item || !value) return;
        item.querySelectorAll('[data-liiv-shop-pane-trigger]').forEach((button) => {
          button.classList.toggle('is-active', button.dataset.liivShopTarget === value);
        });
        item.querySelectorAll('[data-liiv-shop-pane]').forEach((pane) => {
          pane.classList.toggle('is-active', pane.dataset.liivShopPane === value);
        });
      };
      const primeShopPane = (item) => {
        const activeButton =
          item.querySelector('[data-liiv-shop-pane-trigger].is-active') ||
          item.querySelector('[data-liiv-shop-pane-trigger]');
        if (!activeButton) return;
        activateShopPane(item, activeButton.dataset.liivShopTarget);
      };
      const setDesktopOpen = (item, open) => {
        if (!item) return;
        item.classList.toggle('is-open', open);
        item.querySelectorAll('[data-liiv-nav-trigger]').forEach((button) => {
          button.setAttribute('aria-expanded', open ? 'true' : 'false');
        });
        if (open) {
          primeShopPane(item);
        }
      };
      const closeDesktopMenus = () => {
        desktopItems.forEach((item) => setDesktopOpen(item, false));
      };
      const clearDesktopCloseTimer = () => {
        if (desktopCloseTimer) {
          window.clearTimeout(desktopCloseTimer);
          desktopCloseTimer = null;
        }
      };
      // Give the pointer a small forgiveness window when moving from the trigger into the panel.
      const scheduleDesktopClose = (item) => {
        clearDesktopCloseTimer();
        desktopCloseTimer = window.setTimeout(() => {
          setDesktopOpen(item, false);
        }, 180);
      };

      desktopTriggers.forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          clearDesktopCloseTimer();
          const item = trigger.closest('[data-liiv-desktop-item]');
          if (!item) return;
          const isOpen = item.classList.contains('is-open');
          closeDesktopMenus();
          if (!isOpen) {
            setDesktopOpen(item, true);
          }
        });
      });

      root.querySelectorAll('[data-liiv-shop-pane-trigger]').forEach((button) => {
        const item = button.closest('[data-liiv-desktop-item]');
        const handler = () => {
          if (!item) return;
          activateShopPane(item, button.dataset.liivShopTarget);
        };
        button.addEventListener('click', (event) => {
          event.preventDefault();
          handler();
        });
        button.addEventListener('focus', handler);
        button.addEventListener('mouseenter', () => {
          if (window.innerWidth >= 990) {
            handler();
          }
        });
      });

      desktopItems.forEach((item) => {
        item.addEventListener('mouseenter', () => {
          if (window.innerWidth >= 990) {
            clearDesktopCloseTimer();
          }
        });

        item.addEventListener('mouseleave', (event) => {
          if (window.innerWidth < 990) return;
          if (item.contains(event.relatedTarget)) return;
          scheduleDesktopClose(item);
        });

        item.addEventListener('focusin', () => {
          if (window.innerWidth >= 990) {
            clearDesktopCloseTimer();
          }
        });

        item.addEventListener('focusout', (event) => {
          if (window.innerWidth < 990) return;
          if (item.contains(event.relatedTarget)) return;
          scheduleDesktopClose(item);
        });
      });

      document.addEventListener('click', (event) => {
        clearDesktopCloseTimer();
        if (!root.contains(event.target)) {
          closeDesktopMenus();
        }
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          clearDesktopCloseTimer();
          closeDesktopMenus();
        }
      });

      const mobileMenu = root.querySelector('[data-liiv-mobile-menu]');
      const mobileBackdrop = root.querySelector('[data-liiv-mobile-backdrop]');
      const openButton = root.querySelector('[data-liiv-mobile-open]');
      const closeButton = root.querySelector('[data-liiv-mobile-close]');
      const mobileAccordionButtons = [...root.querySelectorAll('[data-liiv-accordion-toggle]')];
      const setMobileAccordionState = (button, expanded) => {
        if (!button) return;
        const panel = button.nextElementSibling;
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        panel?.classList.toggle('is-open', expanded);
      };
      const primeMobileMenu = () => {
        if (!mobileAccordionButtons.length) return;
        if (mobileAccordionButtons.length === 1) {
          setMobileAccordionState(mobileAccordionButtons[0], true);
          return;
        }
        mobileAccordionButtons.forEach((button) => {
          if (button.getAttribute('aria-expanded') === 'true') return;
          setMobileAccordionState(button, false);
        });
      };
      const toggleMobile = (open) => {
        if (!mobileMenu || !mobileBackdrop) return;
        mobileMenu.classList.toggle('is-open', open);
        mobileBackdrop.classList.toggle('is-open', open);
        document.documentElement.classList.toggle('overflow-hidden', open);
        if (open) {
          primeMobileMenu();
        }
      };

      openButton?.addEventListener('click', () => toggleMobile(true));
      closeButton?.addEventListener('click', () => toggleMobile(false));
      mobileBackdrop?.addEventListener('click', () => toggleMobile(false));

      mobileAccordionButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const expanded = button.getAttribute('aria-expanded') === 'true';
          setMobileAccordionState(button, !expanded);
        });
      });
    });
  }

  async function refreshCartCount() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-liiv-cart-count]').forEach((node) => {
        node.textContent = `${cart.item_count || 0}`;
      });
    } catch (error) {
      console.error('Failed to refresh cart count', error);
    }
  }

  function setSubmitting(button, state) {
    if (!button) return;
    if (!button.dataset.originalLabel) {
      button.dataset.originalLabel = button.textContent.trim();
    }
    if (state === 'loading') {
      button.disabled = true;
      button.textContent = 'Adding...';
      return;
    }
    button.disabled = false;
    button.textContent = state === 'success' ? 'Added' : button.dataset.originalLabel;
    if (state === 'success') {
      window.setTimeout(() => {
        button.textContent = button.dataset.originalLabel;
      }, 1400);
    }
  }

  function initCartForms(scope = document) {
    scope.querySelectorAll('form.js-liiv-add-to-cart').forEach((form) => {
      if (form.dataset.liivBound === 'true') return;
      form.dataset.liivBound = 'true';
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        const button = form.querySelector('[type="submit"]');
        setSubmitting(button, 'loading');
        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          if (!response.ok) {
            const payload = await response.json().catch(() => ({}));
            throw new Error(payload.description || payload.message || 'Unable to add this item to cart.');
          }
          await refreshCartCount();
          setSubmitting(button, 'success');
          feedback.show('Added to cart');
        } catch (error) {
          setSubmitting(button, 'idle');
          feedback.show(error.message || 'Unable to add to cart');
        }
      });
    });
  }

  function initQuantityControls(scope = document) {
    scope.querySelectorAll('[data-liiv-qty]').forEach((root) => {
      const input = root.querySelector('input[type="number"]');
      if (!input || root.dataset.liivQtyBound === 'true') return;
      root.dataset.liivQtyBound = 'true';
      root.querySelectorAll('[data-liiv-qty-change]').forEach((button) => {
        button.addEventListener('click', () => {
          const delta = Number(button.dataset.liivQtyChange || 0);
          const current = Number(input.value || 1);
          const min = Number(input.min || 1);
          input.value = String(Math.max(min, current + delta));
        });
      });
    });
  }

  function matchesPdpVariant(variant, filters = {}) {
    if (filters.flavor && variant.flavor !== filters.flavor) return false;
    if (filters.size && variant.size !== filters.size) return false;
    return true;
  }

  function resolvePdpVariantState(variants, state = {}) {
    const exactAvailable = variants.find((variant) => matchesPdpVariant(variant, state) && variant.available);
    if (exactAvailable) return exactAvailable;

    if (state.flavor) {
      const flavorAvailable = variants.find(
        (variant) => matchesPdpVariant(variant, { flavor: state.flavor }) && variant.available
      );
      if (flavorAvailable) return flavorAvailable;
    }

    const exactMatch = variants.find((variant) => matchesPdpVariant(variant, state));
    if (exactMatch) return exactMatch;

    if (state.size) {
      const sizeAvailable = variants.find((variant) => matchesPdpVariant(variant, { size: state.size }) && variant.available);
      if (sizeAvailable) return sizeAvailable;
    }

    return variants.find((variant) => variant.available) || variants[0] || null;
  }

  function updatePdpSelectionCopy(scope, selector, value) {
    scope.querySelectorAll(selector).forEach((node) => {
      node.textContent = value || '';
    });
  }

  function updatePdpPurchaseState(pdpRoot, variant) {
    if (!variant) return;
    const variantInput = pdpRoot.querySelector('input[name="id"]');
    const priceTarget = pdpRoot.querySelector('[data-liiv-price]');
    const compareTarget = pdpRoot.querySelector('[data-liiv-compare-price]');
    const priceNoteTarget = pdpRoot.querySelector('[data-liiv-price-note]');
    const subscribePriceTarget = pdpRoot.querySelector('[data-liiv-subscribe-price]');
    const submitButton = pdpRoot.querySelector('.js-liiv-add-to-cart [type="submit"]');

    if (variantInput) {
      variantInput.value = String(variant.id || variantInput.value);
    }
    if (priceTarget) {
      priceTarget.textContent = variant.price || priceTarget.textContent;
    }
    if (compareTarget) {
      compareTarget.textContent = variant.comparePrice || '';
    }
    if (priceNoteTarget) {
      priceNoteTarget.textContent = variant.priceNote || '';
    }
    if (subscribePriceTarget) {
      subscribePriceTarget.textContent = variant.price || subscribePriceTarget.textContent;
    }
    if (submitButton) {
      const nextLabel = variant.available ? 'Add to Cart' : 'Sold Out';
      submitButton.disabled = !variant.available;
      submitButton.textContent = nextLabel;
      submitButton.dataset.originalLabel = nextLabel;
    }
  }

  function updatePdpGallery(gallery, mediaIndex) {
    if (!gallery || mediaIndex === undefined || mediaIndex === null || mediaIndex === '') return;
    const targetSlide = gallery.querySelector(`[data-liiv-slide][data-liiv-index="${mediaIndex}"]`);
    if (targetSlide) {
      gallery.querySelectorAll('[data-liiv-slide]').forEach((slide) => {
        slide.classList.toggle('is-active', slide === targetSlide);
      });
      targetSlide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      return;
    }
    const targetButton = gallery.querySelector(`[data-liiv-thumb][data-liiv-index="${mediaIndex}"]`);
    targetButton?.click();
  }

  function updatePdpUrl(variant, state = {}) {
    if (!variant?.id || !window.history?.replaceState) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    if (state.flavor) {
      url.searchParams.set('Flavor', state.flavor);
    } else {
      url.searchParams.delete('Flavor');
    }
    if (state.size) {
      url.searchParams.set('Size', state.size);
    } else {
      url.searchParams.delete('Size');
    }
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }

  function currentPreviewThemeId() {
    try {
      return new URL(window.location.href).searchParams.get('preview_theme_id') || '';
    } catch (error) {
      return '';
    }
  }

  function withCurrentPreviewTheme(nextUrl) {
    const previewThemeId = currentPreviewThemeId();
    if (!previewThemeId || !nextUrl) return nextUrl;

    try {
      const url = new URL(nextUrl, window.location.origin);
      if (url.origin !== window.location.origin) return nextUrl;
      url.searchParams.set('preview_theme_id', previewThemeId);
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (error) {
      return nextUrl;
    }
  }

  function preserveThemePreviewLinks(scope = document) {
    const previewThemeId = currentPreviewThemeId();
    if (!previewThemeId) return;

    scope.querySelectorAll('a[href]').forEach((link) => {
      const href = link.getAttribute('href') || '';
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) {
        return;
      }

      try {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) return;
        const navigablePath =
          url.pathname === '/' ||
          url.pathname.startsWith('/collections/') ||
          url.pathname.startsWith('/products/') ||
          url.pathname.startsWith('/pages/') ||
          url.pathname.startsWith('/blogs/') ||
          url.pathname.startsWith('/search');
        if (!navigablePath) return;
        url.searchParams.set('preview_theme_id', previewThemeId);
        link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
      } catch (error) {
        // Ignore malformed storefront links and leave them untouched.
      }
    });
  }

  function updatePdpSizeButtons(root, variants, state) {
    root.querySelectorAll('[data-liiv-size-button]').forEach((button) => {
      const sizeValue = button.dataset.sizeValue || '';
      const exactAvailable = variants.find(
        (variant) => matchesPdpVariant(variant, { flavor: state.flavor, size: sizeValue }) && variant.available
      );
      const exactMatch =
        exactAvailable || variants.find((variant) => matchesPdpVariant(variant, { flavor: state.flavor, size: sizeValue }));
      const caption = button.querySelector('[data-liiv-size-caption]');

      if (!exactMatch) {
        button.hidden = true;
        button.disabled = true;
        button.classList.remove('is-active');
        button.setAttribute('aria-pressed', 'false');
        if (caption) caption.textContent = '';
        return;
      }

      button.hidden = false;
      button.disabled = !exactMatch.available;
      button.classList.toggle('is-active', state.size === sizeValue);
      button.setAttribute('aria-pressed', state.size === sizeValue ? 'true' : 'false');
      if (caption) {
        caption.textContent = exactMatch.available ? exactMatch.price || '' : 'Sold out';
      }
    });
  }

  function updatePdpSizePresentation(root) {
    const sizeGrid = root.querySelector('[data-liiv-size-grid]');
    const sizeHelper = root.querySelector('[data-liiv-size-helper]');
    const visibleButtons = [...root.querySelectorAll('[data-liiv-size-button]')].filter((button) => !button.hidden);
    const isSingleSize = visibleButtons.length <= 1;

    root.dataset.liivSingleSize = isSingleSize ? 'true' : 'false';
    if (sizeGrid) {
      sizeGrid.classList.toggle('is-single', isSingleSize);
    }
    if (sizeHelper) {
      sizeHelper.hidden = !isSingleSize;
    }
  }

  function updateFlavorPickerMedia(target, src, alt = '') {
    if (!target) return;
    let image = target.querySelector('img');

    if (!src) {
      target.hidden = true;
      if (image) {
        image.removeAttribute('src');
        image.alt = alt;
      }
      return;
    }

    if (!image) {
      image = document.createElement('img');
      image.loading = 'lazy';
      target.appendChild(image);
    }

    image.src = src;
    image.alt = alt;
    target.hidden = false;
  }

  function setFlavorPickerOpen(picker, open) {
    if (!picker) return;
    const trigger = picker.querySelector('[data-liiv-flavor-trigger]');
    const menu = picker.querySelector('[data-liiv-flavor-menu]');
    picker.classList.toggle('is-open', open);
    if (trigger) {
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
    if (menu) {
      menu.hidden = !open;
    }
  }

  function syncFlavorPicker(pdpRoot, state = {}) {
    const picker = pdpRoot.querySelector('[data-liiv-flavor-picker]');
    if (!picker) return;

    const flavorOptions = [...picker.querySelectorAll('[data-liiv-flavor-option]')];
    if (!flavorOptions.length) return;

    const trigger = picker.querySelector('[data-liiv-flavor-trigger]');
    const triggerLabel = picker.querySelector('[data-liiv-flavor-trigger-label]');
    const triggerMedia = picker.querySelector('[data-liiv-flavor-trigger-media]');
    const flavorLinkSelect = pdpRoot.querySelector('[data-liiv-flavor-link-select]');

    let activeOption = null;

    if (state.flavor) {
      activeOption = flavorOptions.find((button) => button.dataset.flavorValue === state.flavor) || null;
    }

    if (!activeOption && flavorLinkSelect) {
      const selectedUrl = withCurrentPreviewTheme(flavorLinkSelect.value || '');
      activeOption =
        flavorOptions.find((button) => withCurrentPreviewTheme(button.dataset.flavorUrl || '') === selectedUrl) || null;
    }

    if (!activeOption) {
      activeOption =
        flavorOptions.find((button) => button.dataset.flavorCurrent === 'true' || button.classList.contains('is-active')) ||
        flavorOptions[0];
    }

    flavorOptions.forEach((button) => {
      const active = button === activeOption;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
      if (button.dataset.flavorCurrent !== undefined) {
        button.dataset.flavorCurrent = active ? 'true' : 'false';
      }
    });

    if (!activeOption) return;

    const label = activeOption.dataset.flavorLabel || activeOption.textContent.trim();
    if (triggerLabel) {
      triggerLabel.textContent = label;
    }
    if (trigger) {
      trigger.setAttribute('aria-label', `Choose flavor, current ${label}`);
    }
    updateFlavorPickerMedia(triggerMedia, activeOption.dataset.flavorImage || '', label);
  }

  function syncPdpOptionButtons(root, selector, key, value) {
    root.querySelectorAll(selector).forEach((button) => {
      const active = button.dataset[key] === value;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function initPdpVariants(scope = document) {
    window.__LIIV_PDP_DROPDOWN_READY__ = true;
    scope.querySelectorAll('[data-liiv-variant-picker]').forEach((root) => {
      if (root.dataset.liivVariantBound === 'true') return;
      root.dataset.liivVariantBound = 'true';

      const pdpRoot = root.closest('[data-liiv-pdp]') || root;
      const flavorButtons = [...pdpRoot.querySelectorAll('[data-liiv-flavor-button]')];
      const flavorPicker = pdpRoot.querySelector('[data-liiv-flavor-picker]');
      const flavorTrigger = pdpRoot.querySelector('[data-liiv-flavor-trigger]');
      const flavorOptionButtons = [...pdpRoot.querySelectorAll('[data-liiv-flavor-option]')];
      const flavorSelect = pdpRoot.querySelector('[data-liiv-flavor-select]');
      const flavorLinkSelect = pdpRoot.querySelector('[data-liiv-flavor-link-select]');
      const sizeButtons = [...root.querySelectorAll('[data-liiv-size-button]')];
      const gallery = pdpRoot.querySelector('[data-liiv-gallery]');
      const variantsScript = root.querySelector('[data-liiv-variants-json]');
      const variants = JSON.parse(variantsScript?.textContent || '[]');
      if (!variants.length) return;

      const state = {
        variantId: root.dataset.currentVariantId || '',
        flavor: root.dataset.currentFlavor || '',
        size: root.dataset.currentSize || ''
      };

      const render = ({ updateUrl = false } = {}) => {
        const variant = resolvePdpVariantState(variants, state);
        if (!variant) return;

        state.variantId = String(variant.id);
        state.flavor = variant.flavor || '';
        state.size = variant.size || '';

        root.dataset.currentVariantId = state.variantId;
        root.dataset.currentFlavor = state.flavor;
        root.dataset.currentSize = state.size;

        flavorButtons.forEach((button) => {
          const active = button.dataset.flavorValue === state.flavor;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        if (flavorSelect && flavorSelect.value !== state.flavor) {
          flavorSelect.value = state.flavor;
        }
        syncFlavorPicker(pdpRoot, state);
        updatePdpSizeButtons(root, variants, state);
        updatePdpSizePresentation(root);
        updatePdpSelectionCopy(pdpRoot, '[data-liiv-selected-flavor], [data-liiv-selected-flavor-copy]', state.flavor);
        updatePdpSelectionCopy(pdpRoot, '[data-liiv-selected-size-copy]', state.size);
        updatePdpPurchaseState(pdpRoot, variant);
        updatePdpGallery(gallery, variant.mediaIndex);

        if (updateUrl) {
          updatePdpUrl(variant, state);
        }
      };

      flavorButtons.forEach((button) => {
        button.addEventListener('click', () => {
          state.flavor = button.dataset.flavorValue || '';
          render({ updateUrl: true });
          setFlavorPickerOpen(flavorPicker, false);
        });
      });

      flavorSelect?.addEventListener('change', () => {
        state.flavor = flavorSelect.value || '';
        render({ updateUrl: true });
        setFlavorPickerOpen(flavorPicker, false);
      });

      const navigateToFlavor = (url) => {
        const nextUrl = withCurrentPreviewTheme(url || '');
        if (!nextUrl) return;
        const currentUrl = `${window.location.pathname}${window.location.search}`;
        if (nextUrl === currentUrl || nextUrl === window.location.pathname) return;
        window.location.assign(nextUrl);
      };

      flavorLinkSelect?.addEventListener('change', () => {
        navigateToFlavor(flavorLinkSelect.value || '');
      });

      flavorOptionButtons.forEach((button) => {
        if (!button.dataset.flavorUrl) return;
        button.addEventListener('click', () => {
          setFlavorPickerOpen(flavorPicker, false);
          navigateToFlavor(button.dataset.flavorUrl || '');
        });
      });

      flavorTrigger?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        setFlavorPickerOpen(flavorPicker, !flavorPicker?.classList.contains('is-open'));
      });

      if (flavorPicker) {
        setFlavorPickerOpen(flavorPicker, false);

        document.addEventListener('click', (event) => {
          if (!(event.target instanceof Node) || flavorPicker.contains(event.target)) return;
          setFlavorPickerOpen(flavorPicker, false);
        });

        document.addEventListener('keydown', (event) => {
          if (event.key === 'Escape') {
            setFlavorPickerOpen(flavorPicker, false);
          }
        });
      }

      sizeButtons.forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled) return;
          state.size = button.dataset.sizeValue || '';
          render({ updateUrl: true });
        });
      });

      render();
    });
  }

  function initGalleries(scope = document) {
    scope.querySelectorAll('[data-liiv-gallery]').forEach((root) => {
      if (root.dataset.liivGalleryBound === 'true') return;
      root.dataset.liivGalleryBound = 'true';
      const slides = [...root.querySelectorAll('[data-liiv-slide]')];
      const thumbs = [...root.querySelectorAll('[data-liiv-thumb]')];
      let index = slides.findIndex((slide) => slide.classList.contains('is-active'));
      if (index < 0) index = 0;

      const render = () => {
        slides.forEach((slide, slideIndex) => slide.classList.toggle('is-active', slideIndex === index));
        thumbs.forEach((thumb, thumbIndex) => thumb.classList.toggle('is-active', thumbIndex === index));
      };

      let ticking = false;
      const syncFromScroll = () => {
        if (!slides.length) return;
        const rootRect = root.getBoundingClientRect();
        let nextIndex = index;
        let closestDistance = Number.POSITIVE_INFINITY;

        slides.forEach((slide, slideIndex) => {
          const distance = Math.abs(slide.getBoundingClientRect().left - rootRect.left);
          if (distance < closestDistance) {
            closestDistance = distance;
            nextIndex = slideIndex;
          }
        });

        if (nextIndex !== index) {
          index = nextIndex;
          render();
        }
      };

      thumbs.forEach((thumb, thumbIndex) => {
        thumb.addEventListener('click', () => {
          index = thumbIndex;
          render();
        });
      });

      root.querySelector('[data-liiv-gallery-prev]')?.addEventListener('click', () => {
        index = (index - 1 + slides.length) % slides.length;
        render();
      });

      root.querySelector('[data-liiv-gallery-next]')?.addEventListener('click', () => {
        index = (index + 1) % slides.length;
        render();
      });

      root.addEventListener(
        'scroll',
        () => {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(() => {
            syncFromScroll();
            ticking = false;
          });
        },
        { passive: true }
      );

      render();
    });
  }

  function initVideoToggles(scope = document) {
    scope.querySelectorAll('[data-liiv-video-toggle]').forEach((button) => {
      if (button.dataset.liivBound === 'true') return;
      button.dataset.liivBound = 'true';
      const frame = button.closest('[data-liiv-video-frame]');
      const media = frame?.querySelector('video');
      if (!media) {
        button.hidden = true;
        return;
      }

      const render = () => {
        button.classList.toggle('is-active', !media.muted);
        button.setAttribute(
          'aria-label',
          media.muted ? button.dataset.liivLabelMuted || 'Unmute video' : button.dataset.liivLabelUnmuted || 'Mute video'
        );
      };

      button.addEventListener('click', () => {
        media.muted = !media.muted;
        media.defaultMuted = media.muted;
        render();
      });

      render();
    });
  }

  function boot() {
    preserveThemePreviewLinks();
    initTabs();
    initCarousels();
    initAnnouncements();
    initNavigation();
    initCartForms();
    initQuantityControls();
    initPdpVariants();
    initGalleries();
    initVideoToggles();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    preserveThemePreviewLinks(event.target instanceof HTMLElement ? event.target : document);
  });
})();
